import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronIcon, PlusIcon, SearchIcon } from '@/components/catalog/icons';
import { NewCampaignModal } from '@/components/marketing/campaigns/NewCampaignModal';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  type ComboboxOption,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateTime } from '@/lib/format';
import { CAMPAIGN_STATUS_LABEL, campaignStatusTone, percent } from '@/lib/marketing';
import type {
  MarketingCampaignKind,
  MarketingCampaignListParams,
  MarketingCampaignStatus,
  MarketingCampaignSummary,
  MarketingOverview,
  Paginated,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

const PER_PAGE = 25;
const MODULE = 'marketing-campaigns' as const;
const OVERVIEW_DAYS = 30;

const STATUSES: readonly MarketingCampaignStatus[] = [
  'draft',
  'pending_approval',
  'scheduled',
  'sending',
  'paused',
  'completed',
  'cancelled',
  'failed',
];
const STATUS_OPTIONS: ComboboxOption[] = STATUSES.map((s) => ({ value: s, label: CAMPAIGN_STATUS_LABEL[s] }));
const KIND_OPTIONS: ComboboxOption[] = [
  { value: 'broadcast', label: 'Broadcast' },
  { value: 'sequence', label: 'Sequence' },
];
const KIND_LABEL: Record<MarketingCampaignKind, string> = { broadcast: 'Broadcast', sequence: 'Sequence' };

/** Statuses the builder handles; everything else opens the report. */
const BUILDER_STATUSES: readonly MarketingCampaignStatus[] = ['draft', 'paused', 'pending_approval'];
const DELETABLE: readonly MarketingCampaignStatus[] = ['draft', 'cancelled'];

const TABLE_STYLE = { minWidth: 1180 };

function isStatus(v: string | null): v is MarketingCampaignStatus {
  return v !== null && (STATUSES as readonly string[]).includes(v);
}
function isKind(v: string | null): v is MarketingCampaignKind {
  return v === 'broadcast' || v === 'sequence';
}

/**
 * A multi-value URL filter (owner 2026-10-28): one value or a comma-separated
 * list, unknown members dropped, duplicates removed. A single-value bookmark
 * reads back as a one-item selection.
 */
function csvOf<T extends string>(raw: string | null, accept: (v: string) => v is T): T[] {
  if (!raw) return [];
  return [...new Set(raw.split(',').map((part) => part.trim()))].filter(accept);
}

function campaignHref(c: { id: number; status: MarketingCampaignStatus }): string {
  return BUILDER_STATUSES.includes(c.status) ? `/marketing/campaigns/${c.id}` : `/marketing/campaigns/${c.id}/report`;
}

function ratio(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

function OverviewStrip() {
  const [data, setData] = useState<MarketingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getMarketingOverview(OVERVIEW_DAYS, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setData(result.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught.message : 'Could not reach the server.');
      });
    return () => controller.abort();
  }, []);

  const tiles: { label: string; value: string; estimate?: boolean }[] = data
    ? [
        { label: 'Emails sent', value: data.emailsSent.toLocaleString() },
        { label: 'Open rate', value: percent(data.uniqueOpenRate), estimate: true },
        { label: 'Click rate', value: percent(data.uniqueClickRate) },
        { label: 'Unsubscribes', value: data.unsubscribes.toLocaleString() },
        { label: 'Bounces', value: data.bounces.toLocaleString() },
      ]
    : [];

  return (
    <Panel aria-labelledby="marketing-overview-heading">
      <PanelHeader id="marketing-overview-heading" title={`Last ${OVERVIEW_DAYS} days`} />
      {error ? (
        <div className="border-t border-border px-4 py-3">
          <Notice tone="danger" role="alert">
            Couldn&rsquo;t load the overview. {error}
          </Notice>
        </div>
      ) : (
        // Five tiles on a gap-px hairline grid: below `lg` the last row is short
        // by one cell, and the empty cell shows the `bg-border` fill as a grey
        // block. The last tile spans two columns there so the row closes.
        <dl className="m-0 grid grid-cols-2 gap-px bg-border sm:grid-cols-3 lg:grid-cols-5 [&>*:last-child]:col-span-2 lg:[&>*:last-child]:col-span-1">
          {data === null
            ? Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="grid gap-2 bg-surface px-4 py-3">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))
            : tiles.map((tile) => (
                <div key={tile.label} className="grid gap-1 bg-surface px-4 py-3">
                  <dt className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
                    {tile.label}
                    {tile.estimate && (
                      <Badge tone="neutral" variant="outline" title="Opens are estimated: image blocking and privacy proxies distort them.">
                        Estimate
                      </Badge>
                    )}
                  </dt>
                  <dd className="m-0 text-xl font-semibold text-fg tabular">{tile.value}</dd>
                </div>
              ))}
        </dl>
      )}
    </Panel>
  );
}

export function CampaignsPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead(MODULE);
  const canCreate = ability.canCreate(MODULE);
  const canDelete = ability.canDelete(MODULE);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const paramsKey = searchParams.toString();
  const query = useMemo(() => {
    const params = new URLSearchParams(paramsKey);
    const page = Number.parseInt(params.get('page') ?? '1', 10);
    return {
      search: params.get('q') ?? '',
      statuses: csvOf(params.get('status'), isStatus),
      kinds: csvOf(params.get('kind'), isKind),
      page: Number.isFinite(page) && page > 0 ? page : 1,
    };
  }, [paramsKey]);

  const [searchDraft, setSearchDraft] = useState(query.search);
  const [result, setResult] = useState<Paginated<MarketingCampaignSummary> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Loading is derived: the request in force differs from the last one that settled.
  const requestKey = `${paramsKey}#${reloadToken}`;
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const loading = settledKey !== requestKey;
  const [newOpen, setNewOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<MarketingCampaignSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const applyPatch = useCallback(
    (patch: Partial<{ q: string; status: string; kind: string; page: number }>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === '' || value === undefined || (key === 'page' && value === 1)) next.delete(key);
            else next.set(key, String(value));
          }
          if (!('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Debounce the search box into the URL.
  useEffect(() => {
    if (searchDraft.trim() === query.search) return undefined;
    const timer = window.setTimeout(() => applyPatch({ q: searchDraft.trim() }), 250);
    return () => window.clearTimeout(timer);
  }, [searchDraft, query.search, applyPatch]);

  useEffect(() => {
    if (!canRead) return undefined;
    const controller = new AbortController();
    const params: MarketingCampaignListParams = { page: query.page, perPage: PER_PAGE };
    if (query.search) params.search = query.search;
    if (query.statuses.length > 0) params.status = query.statuses.join(',');
    if (query.kinds.length > 0) params.kind = query.kinds.join(',');
    const key = requestKey;
    api
      .listMarketingCampaigns(params, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setResult(next);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught.message : 'Could not reach the server.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettledKey(key);
      });
    return () => controller.abort();
  }, [canRead, query, requestKey]);

  if (!canRead) {
    return (
      <div className="grid min-w-0 gap-4">
        <PageHeader eyebrow="B2B Marketing" title="Campaigns" />
        <Panel>
          <EmptyState
            title="Campaigns are restricted"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </div>
    );
  }

  const rows = result?.data ?? [];
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (query.page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;
  const anyFilter = query.search !== '' || query.statuses.length > 0 || query.kinds.length > 0;
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total.toLocaleString()} ${total === 1 ? 'campaign' : 'campaigns'}`;

  async function duplicate(campaign: MarketingCampaignSummary) {
    setDuplicating(campaign.id);
    setRowError(null);
    try {
      const copy = await api.duplicateMarketingCampaign(campaign.id);
      navigate(`/marketing/campaigns/${copy.data.id}`);
    } catch (caught) {
      setRowError(
        caught instanceof ApiError
          ? `Couldn’t duplicate ${campaign.name}. ${caught.formMessage ?? caught.message}`
          : `Couldn’t duplicate ${campaign.name}. Could not reach the server.`,
      );
      setDuplicating(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteMarketingCampaign(pendingDelete.id);
      setPendingDelete(null);
      setReloadToken((n) => n + 1);
    } catch (caught) {
      setDeleteError(
        caught instanceof ApiError
          ? (caught.formMessage ?? caught.message)
          : 'Could not reach the server. The campaign was not deleted.',
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="grid min-w-0 gap-4">
      <PageHeader
        eyebrow="B2B Marketing"
        title="Campaigns"
        actions={
          canCreate ? (
            <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setNewOpen(true)}>
              New campaign
            </Button>
          ) : undefined
        }
      />

      <OverviewStrip />

      <Panel className="min-w-0" aria-labelledby="campaigns-heading">
        <PanelHeader id="campaigns-heading" title="All campaigns" description={countLabel} />
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Search campaigns" hideLabel>
              {({ id }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search campaigns…"
                  leadingIcon={<SearchIcon />}
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-48">
            <Field label="Filter by status" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={query.statuses}
                  onChange={(values) => applyPatch({ status: values.join(',') })}
                  options={STATUS_OPTIONS}
                  placeholder="Any status"
                  searchPlaceholder="Search statuses…"
                />
              )}
            </Field>
          </div>
          <div className="w-full min-w-0 sm:w-40">
            <Field label="Filter by kind" hideLabel>
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  values={query.kinds}
                  onChange={(values) => applyPatch({ kind: values.join(',') })}
                  options={KIND_OPTIONS}
                  placeholder="Any kind"
                  searchPlaceholder="Search kinds…"
                />
              )}
            </Field>
          </div>
          {anyFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchDraft('');
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {error && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load campaigns.</>}>
              <span className="[overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}
        {rowError && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" className="[overflow-wrap:anywhere]">
              {rowError}
            </Notice>
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'min-w-0 transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {initialLoad ? (
            <TableWrap frozenHeader>
              <Table style={TABLE_STYLE}>
                <CampaignsTableHead />
                <TBody>
                  {Array.from({ length: 6 }, (_, i) => (
                    <TR key={i}>
                      {Array.from({ length: 10 }, (__, j) => (
                        <TD key={j} pinned={j === 9}>
                          <Skeleton className={j === 0 ? 'h-3 w-40' : 'h-3 w-14'} />
                        </TD>
                      ))}
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          ) : rows.length === 0 ? (
            error ? null : (
              <EmptyState
                title={anyFilter ? 'No campaigns match these filters' : 'No campaigns yet'}
                description={
                  anyFilter
                    ? 'Clear the filters to see every campaign.'
                    : canCreate
                      ? 'Create a campaign to send a broadcast or a sequence.'
                      : undefined
                }
                action={
                  !anyFilter && canCreate ? (
                    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setNewOpen(true)}>
                      New campaign
                    </Button>
                  ) : undefined
                }
              />
            )
          ) : (
            <TableWrap frozenHeader>
              <Table style={TABLE_STYLE}>
                <CampaignsTableHead />
                <TBody>
                  {rows.map((campaign) => {
                    const href = campaignHref(campaign);
                    const stats = campaign.stats;
                    const recipients = stats.recipients > 0 ? stats.recipients : campaign.recipientEstimate;
                    const when = campaign.startedAt ?? campaign.scheduledAt;
                    return (
                      <TR key={campaign.id} interactive onClick={() => navigate(href)} className="cursor-pointer">
                        <TD>
                          <Link
                            to={href}
                            className="font-medium text-fg [overflow-wrap:anywhere] hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {campaign.name}
                          </Link>
                          {campaign.abEnabled && <span className="ml-1.5 text-xs text-fg-muted">A/B</span>}
                        </TD>
                        <TD className="text-fg-secondary">{KIND_LABEL[campaign.kind]}</TD>
                        <TD>
                          <Badge tone={campaignStatusTone(campaign.status)}>{CAMPAIGN_STATUS_LABEL[campaign.status]}</Badge>
                        </TD>
                        <TD className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
                          {campaign.emailAccount ? (
                            <EmailLink address={campaign.emailAccount.fromEmail} className="text-fg-secondary" />
                          ) : (
                            <span className="text-fg-subtle">—</span>
                          )}
                        </TD>
                        <TD className="text-xs text-fg-secondary tabular">
                          {when ? (
                            <>
                              <span className="block">{formatDateTime(when)}</span>
                              <span className="text-fg-subtle">{campaign.startedAt ? 'Started' : 'Scheduled'}</span>
                            </>
                          ) : (
                            <span className="text-fg-subtle">—</span>
                          )}
                        </TD>
                        <TD numeric className="tabular">
                          {recipients === null ? <span className="text-fg-subtle">—</span> : recipients.toLocaleString()}
                        </TD>
                        <TD numeric className="tabular">{stats.sent.toLocaleString()}</TD>
                        <TD numeric className="tabular" title="Estimated">
                          {percent(ratio(stats.uniqueOpens, stats.sent))}
                        </TD>
                        <TD numeric className="tabular">{percent(ratio(stats.uniqueClicks, stats.sent))}</TD>
                        {/* Pinned: at 1440 the 1180px floor puts this lane past the
                            scroller's edge, so Delete was unreachable unseen. */}
                        <TD numeric pinned>
                          <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                            {canCreate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                loading={duplicating === campaign.id}
                                onClick={() => void duplicate(campaign)}
                                aria-label={`Duplicate ${campaign.name}`}
                              >
                                Duplicate
                              </Button>
                            )}
                            {canDelete && DELETABLE.includes(campaign.status) && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                  setDeleteError(null);
                                  setPendingDelete(campaign);
                                }}
                                aria-label={`Delete ${campaign.name}`}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No campaigns to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {query.page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={query.page <= 1}
                onClick={() => applyPatch({ page: query.page - 1 })}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={query.page >= totalPages}
                onClick={() => applyPatch({ page: query.page + 1 })}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      {newOpen && <NewCampaignModal open onClose={() => setNewOpen(false)} />}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : 'Delete campaign?'}
        description={
          pendingDelete?.status === 'cancelled'
            ? 'The campaign, its send history and its report will be deleted. This can’t be undone.'
            : 'The draft, its steps and its audience will be deleted. This can’t be undone.'
        }
        confirmLabel="Delete campaign"
        tone="danger"
        loading={deleting}
        error={deleteError}
      />
    </div>
  );
}

function CampaignsTableHead() {
  return (
    <THead>
      <TR>
        <TH>Campaign</TH>
        <TH>Kind</TH>
        <TH>Status</TH>
        <TH>Sender</TH>
        <TH>Scheduled / started</TH>
        <TH numeric>Recipients</TH>
        <TH numeric>Sent</TH>
        <TH numeric>Open rate (est.)</TH>
        <TH numeric>Click rate</TH>
        <TH numeric pinned>
          <span className="sr-only">Actions</span>
        </TH>
      </TR>
    </THead>
  );
}
